import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import api from '@crm-src/lib/api'

export interface Invoice {
  /** A quote mirrored from a reseller's portal request: the CRM converts or
   * deletes it (removing the reseller's row too), never edits it. */
  locked?: boolean
  /** The reseller a document is addressed to — set on every mirrored request. */
  bill_to_reseller?: { id: number; name: string; code?: string } | null
  /** The reseller's portal request behind a mirrored quote: which of its
   * customers it is for, and who asked. */
  reseller_quote?: {
    id: number
    number: string
    status: string
    effective_status?: string
    requested_by_email?: string | null
    customer_id?: number | null
    customer_name?: string | null
  } | null
  id: number;
  invoice_number: string;
  customer_id?: number;
  customer_name?: string;
  customer_email?: string;
  customer_company_name?: string;
  customer_deal_id?: number;
  customer_deal_title?: string;
  issue_date: string;
  due_date: string;
  paid_date?: string;
  // Service period (the window the invoice bills for) — invoice-only, may be
  // absent. Serialized by crm-api's InvoiceSerializer as MM/DD/YYYY strings.
  service_from?: string;
  service_to?: string;
  payment_status: 'unpaid' | 'paid' | 'overdue';
  status?: 'draft' | 'sent' | 'paid' | 'unpaid' | 'overdue' | 'expired' | 'converted' | 'rejected' | string;
  total_amount: number;
  sub_total?: number;
  tax_percentage?: number;
  tax_amount?: number;
  balance_due?: number;
  overdue?: boolean;
  days_overdue?: number;
  files?: InvoiceFile[];
  created_at: string;
  updated_at: string;
  created_by?: {
    id: number;
    full_name: string;
    email: string;
  } | null;
  customer?: {
    id: number;
    full_name: string;
    email: string;
    company_name?: string;
  };
  customer_deal?: {
    id: number;
    title: string;
    amount: number;
    status: string;
  };
  formatted_amounts?: {
    total: string;
    balance_due: string;
  };
}

interface InvoiceFile {
  id: number;
  filename: string;
  url: string;
  size: number;
  uploaded_at: string;
}

export interface InvoiceStats {
  total_invoices: number;
  paid_invoices: number;
  unpaid_invoices: number;
  overdue_invoices: number;
  total_outstanding: number;
  total_overdue: number;
  total_paid_this_month: number;
}

export interface InvoicePagination {
  current_page: number;
  per_page: number;
  total_pages: number;
  total_count: number;
  has_next: boolean;
  has_prev: boolean;
}

interface InvoicesState {
  invoices: Invoice[];
  stats: InvoiceStats | null;
  pagination: InvoicePagination | null;
  isLoading: boolean;
  lastFetched: number | null;
  error: string | null;
}

const initialState: InvoicesState = {
  invoices: [],
  stats: null,
  pagination: null,
  isLoading: false,
  lastFetched: null,
  error: null,
}

// Cache duration in milliseconds (30 seconds)
const CACHE_DURATION = 30 * 1000

/**
 * Every param `GET /api/v1/invoices` accepts from the list pages.
 *
 * `sort` / `dir` are the CANONICAL names the crm-api reads; `sort_by` /
 * `sort_direction` are the legacy aliases `Sortable` still normalises, kept
 * only because older callers send them. New code emits `sort` + `dir` via
 * `buildTableParams`.
 *
 * `payment_status` and `created_by_id` are the two DECLARED facets (see the
 * controller's `filterable`); `status`, `customer_id` and `document_type`
 * are bespoke server-side handling, not facets. Both kinds are request params
 * all the same, which is why they sit in one interface.
 */
export interface InvoiceListFilters {
  page?: number;
  per_page?: number;
  search?: string;
  status?: string;
  payment_status?: string;
  created_by_id?: string;
  start_date?: string;
  end_date?: string;
  sort?: string;
  dir?: 'asc' | 'desc';
  sort_by?: string;
  sort_direction?: string;
  document_type?: 'invoice' | 'quote';
  customer_id?: string | number;
}

// Fetch invoices with optional filters
export const fetchInvoices = createAsyncThunk(
  'invoices/fetchInvoices',
  async (params: InvoiceListFilters = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { invoices: InvoicesState }
      const now = Date.now()
      
      // Check if we have cached data that's still fresh and no filters
      if (
        Object.keys(params).length === 0 &&
        state.invoices.invoices.length > 0 &&
        state.invoices.lastFetched &&
        (now - state.invoices.lastFetched < CACHE_DURATION)
      ) {
        return {
          invoices: state.invoices.invoices,
          pagination: state.invoices.pagination,
          cached: true
        }
      }
      
      const response = await api.get('/invoices', { params })
      
      return {
        invoices: response.data.data || response.data,
        pagination: response.data.meta?.pagination || null,
        cached: false,
        lastFetched: now
      }
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.error || 'Failed to fetch invoices')
    }
  }
)

// Create new invoice
export const createInvoice = createAsyncThunk(
  'invoices/createInvoice',
  async (params: { invoiceData: Partial<Invoice>; file?: File }, { rejectWithValue }) => {
    try {
      
      // If file is provided, use FormData to send both invoice and file
      if (params.file) {
        const formData = new FormData()
        
        // Add invoice data fields
        Object.entries(params.invoiceData).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            formData.append(`invoice[${key}]`, value.toString())
          }
        })
        
        // Add the file
        formData.append('file', params.file)
        
        const response = await api.post('/invoices', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        return response.data.data || response.data
      } else {
        // No file, send as JSON
        const response = await api.post('/invoices', { invoice: params.invoiceData })
        return response.data.data || response.data
      }
    } catch (error: any) {
      // Return the full error response structure so the UI can access specific error messages
      return rejectWithValue({
        error: error?.response?.data?.error || 'Failed to create invoice',
        errors: error?.response?.data?.errors || [error?.response?.data?.error || 'Failed to create invoice'],
        status: error?.response?.data?.status || 'error',
        response: error?.response
      })
    }
  }
)

// Update invoice
export const updateInvoice = createAsyncThunk(
  'invoices/updateInvoice',
  async ({ id, data, file }: { id: number; data: Partial<Invoice>; file?: File }, { rejectWithValue }) => {
    try {
      
      // If file is provided, use FormData to send both invoice and file
      if (file) {
        const formData = new FormData()
        
        // Add invoice data fields
        Object.entries(data).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            formData.append(`invoice[${key}]`, value.toString())
          }
        })
        
        // Add the file
        formData.append('file', file)
        
        const response = await api.patch(`/invoices/${id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        return response.data.data || response.data
      } else {
        // No file, send as JSON
        const response = await api.patch(`/invoices/${id}`, { invoice: data })
        return response.data.data || response.data
      }
    } catch (error: any) {
      // Symmetric with createInvoice: preserve the full server validation
      // array so thunk-based update callers (e.g. inline edits) can surface
      // every message, not just the top-level summary string.
      return rejectWithValue({
        error: error?.response?.data?.error || 'Failed to update invoice',
        errors: error?.response?.data?.errors || [error?.response?.data?.error || 'Failed to update invoice'],
        status: error?.response?.data?.status || 'error',
        response: error?.response
      })
    }
  }
)

// Delete invoice
export const deleteInvoice = createAsyncThunk(
  'invoices/deleteInvoice',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.delete(`/invoices/${id}`)
      return id
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.error || 'Failed to delete invoice')
    }
  }
)

// Bulk delete invoices
export const bulkDeleteInvoices = createAsyncThunk(
  'invoices/bulkDeleteInvoices',
  async (invoiceIds: number[], { rejectWithValue }) => {
    try {
      const response = await api.delete('/invoices/bulk_delete', {
        data: { invoice_ids: invoiceIds }
      })
      // The ids crm-api ACTUALLY soft-deleted, not the ids sent: a quote mirrored
      // from a reseller's request is left alone (reported as `locked`) and must
      // stay in the list. An older crm-api without deleted_ids: trust the request.
      const reported = response.data?.data?.deleted_ids
      return {
        deletedIds: Array.isArray(reported) ? (reported as number[]) : invoiceIds,
        response: response.data
      }
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.error || 'Failed to bulk delete invoices')
    }
  }
)

// Upload invoice file
export const uploadInvoiceFile = createAsyncThunk(
  'invoices/uploadInvoiceFile',
  async ({ id, file }: { id: number; file: File }, { rejectWithValue }) => {
    try {
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await api.post(`/invoices/${id}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return { invoiceId: id, file: response.data.file }
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.error || 'Failed to upload invoice file')
    }
  }
)

// Mark invoice as paid
export const markInvoiceAsPaid = createAsyncThunk(
  'invoices/markInvoiceAsPaid',
  async ({ id, paid_amount, paid_date }: { id: number; paid_amount?: number; paid_date?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/invoices/${id}/mark_as_paid`, {
        paid_amount,
        paid_date,
      })
      return response.data.data || response.data.invoice || response.data
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.error || 'Failed to mark invoice as paid')
    }
  }
)

// Mark invoice as unpaid
export const markInvoiceAsUnpaid = createAsyncThunk(
  'invoices/markInvoiceAsUnpaid',
  async ({ id }: { id: number }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/invoices/${id}/mark_as_unpaid`)
      return response.data.data || response.data.invoice || response.data
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.error || 'Failed to mark invoice as unpaid')
    }
  }
)

// Fetch invoice statistics
export const fetchInvoiceStats = createAsyncThunk(
  'invoices/fetchInvoiceStats',
  async (params: { document_type?: 'invoice' | 'quote' } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/invoices/stats', { params })
      return response.data.data || response.data
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.error || 'Failed to fetch invoice statistics')
    }
  }
)

const invoicesSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    // Clear cache to force fresh data on next request
    clearCache: (state) => {
      state.lastFetched = null
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch invoices
      .addCase(fetchInvoices.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        // If we got cached data, just update loading state
        if (action.payload.cached) {
          state.isLoading = false
          return
        }
        
        state.invoices = action.payload.invoices
        state.pagination = action.payload.pagination
        state.lastFetched = action.payload.lastFetched || Date.now()
        state.isLoading = false
        state.error = null
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Create invoice
      .addCase(createInvoice.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.invoices.unshift(action.payload)
        state.isLoading = false
        state.error = null
        // Clear cache to ensure fresh data on next fetch
        state.lastFetched = null
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Update invoice
      .addCase(updateInvoice.pending, (state) => {
        state.error = null
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        const index = state.invoices.findIndex(inv => inv.id === action.payload.id)
        if (index !== -1) {
          state.invoices[index] = action.payload
        }
        state.error = null
        // Clear cache to ensure fresh data on next fetch
        state.lastFetched = null
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.error = action.payload as string
      })
      
      // Delete invoice
      .addCase(deleteInvoice.pending, (state) => {
        state.error = null
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.invoices = state.invoices.filter(inv => inv.id !== action.payload)
        state.error = null
        // Clear cache to ensure fresh data on next fetch
        state.lastFetched = null
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.error = action.payload as string
      })

      // Bulk delete invoices
      .addCase(bulkDeleteInvoices.pending, (state) => {
        state.error = null
      })
      .addCase(bulkDeleteInvoices.fulfilled, (state, action) => {
        // Remove all deleted invoices from the state
        state.invoices = state.invoices.filter(inv => !action.payload.deletedIds.includes(inv.id))
        state.error = null
        // Clear cache to ensure fresh data on next fetch
        state.lastFetched = null
      })
      .addCase(bulkDeleteInvoices.rejected, (state, action) => {
        state.error = action.payload as string
      })
      
      // Upload invoice file
      .addCase(uploadInvoiceFile.pending, (state) => {
        state.error = null
      })
      .addCase(uploadInvoiceFile.fulfilled, (state, action) => {
        const invoice = state.invoices.find(inv => inv.id === action.payload.invoiceId)
        if (invoice) {
          if (!invoice.files) {
            invoice.files = []
          }
          invoice.files.push(action.payload.file)
        }
        state.error = null
      })
      .addCase(uploadInvoiceFile.rejected, (state, action) => {
        state.error = action.payload as string
      })
      
      // Mark invoice as paid
      .addCase(markInvoiceAsPaid.pending, (state) => {
        state.error = null
      })
      .addCase(markInvoiceAsPaid.fulfilled, (state, action) => {
        const index = state.invoices.findIndex(inv => inv.id === action.payload.id)
        if (index !== -1) {
          state.invoices[index] = action.payload
        }
        state.error = null
        // Clear cache to ensure fresh data on next fetch
        state.lastFetched = null
      })
      .addCase(markInvoiceAsPaid.rejected, (state, action) => {
        state.error = action.payload as string
      })
      
      // Mark invoice as unpaid
      .addCase(markInvoiceAsUnpaid.pending, (state) => {
        state.error = null
      })
      .addCase(markInvoiceAsUnpaid.fulfilled, (state, action) => {
        const index = state.invoices.findIndex(inv => inv.id === action.payload.id)
        if (index !== -1) {
          state.invoices[index] = action.payload
        }
        state.error = null
        // Clear cache to ensure fresh data on next fetch
        state.lastFetched = null
      })
      .addCase(markInvoiceAsUnpaid.rejected, (state, action) => {
        state.error = action.payload as string
      })
      
      // Fetch invoice stats
      .addCase(fetchInvoiceStats.pending, (state) => {
        state.error = null
      })
      .addCase(fetchInvoiceStats.fulfilled, (state, action) => {
        state.stats = action.payload
        state.error = null
      })
      .addCase(fetchInvoiceStats.rejected, (state, action) => {
        state.error = action.payload as string
      })
  },
})

export const { clearCache, clearError } = invoicesSlice.actions
export default invoicesSlice.reducer
